import React, { forwardRef, Ref, useImperativeHandle, useRef, useState } from 'react';
import SelectListModal from './SelectListModal';
import { asyncSimpleModal } from '../../../lib/common';
import ExcelTable from '../../table/ExcelTable';
import { AntdColumn, AntdColumnUtilType, Option, SearchOption, TableListData } from '../../../types/common/table';
import { SelectListStyle } from './style';

export interface SelectListProps<Item, Filter> {
  value?: Item[];
  max?: number;
  excelTemplateUrl: string;
  queryKey: string[];
  fetcher: (option: SearchOption<Filter>) => Promise<TableListData<Item>>;
  columns: (utils: AntdColumnUtilType<Item, Filter>) => AntdColumn<Item>[];
  initialOption?: Option<Filter>;
  canDrag?: boolean;
  formatBeforeChange?: (value: Item[]) => Item[];
  onChange?: (value: Item[]) => void;
  onExcelUpload: (file: File) => void;
  onDownloadExcel: (idList: number[]) => void;
}

const SelectListInner = <Item extends { id: number; invalid?: boolean }, Filter extends Record<string, unknown>>(
  props: SelectListProps<Item, Filter>,
  ref: Ref<{ value?: Item[]; onChange?: (value: Item[]) => void }>,
): JSX.Element => {
  const {
    queryKey,
    fetcher,
    initialOption,
    excelTemplateUrl,
    columns,
    value = [],
    max,
    canDrag = false,
    formatBeforeChange,
    onChange,
    onExcelUpload,
    onDownloadExcel,
  } = props;

  const excelTableRef = useRef<{ resetOption: () => void } | null>(null);
  const [isShowSelectListModal, setIsShowSelectListModal] = useState(false);

  useImperativeHandle(ref, () => ({
    value,
    onChange,
  }));

  const handleAddItem = async (selectedList: Item[]): Promise<void> => {
    if (max && selectedList.length > max) {
      await asyncSimpleModal({
        type: 'error',
        title: '아이템 개수 초과',
        message: `아이템 개수는 최대 ${max}개 입니다.`,
      });
      return;
    }

    const list = formatBeforeChange ? formatBeforeChange(selectedList) : selectedList;

    onChange && onChange(list);
    setIsShowSelectListModal(false);
    excelTableRef.current?.resetOption();
  };

  const handleDragEnd = ({ list }: { list: Item[] }): void => onChange && onChange(list);

  const handleDeleteList = (targetIdList: number[]): void => {
    const nextList = value.filter((item) => !targetIdList.includes(item.id));
    onChange && onChange(nextList);
  };

  return (
    <SelectListStyle className="amondz-input">
      <ExcelTable
        ref={excelTableRef}
        columns={columns}
        list={value}
        excelTemplateUrl={excelTemplateUrl}
        rowClassName={(record): 'invalid' | null => (record.invalid ? 'invalid' : null)}
        canDrag={canDrag}
        onAddItem={(): void => setIsShowSelectListModal(true)}
        onExcelUpload={onExcelUpload}
        onDownloadExcel={onDownloadExcel}
        onDragEnd={handleDragEnd}
        onDeleteList={handleDeleteList}
        initialOption={initialOption}
      />
      {isShowSelectListModal && (
        <SelectListModal
          value={value}
          queryKey={queryKey}
          fetcher={fetcher}
          onOk={handleAddItem}
          onClose={(): void => setIsShowSelectListModal(false)}
          initialOption={initialOption}
          columns={columns}
        />
      )}
    </SelectListStyle>
  );
};

const SelectList = forwardRef(SelectListInner) as <
  Item extends { id: number; invalid?: boolean },
  Filter extends Record<string, unknown>,
>(
  props: SelectListProps<Item, Filter>,
  ref: Ref<{ value?: Item[]; onChange?: (value: Item[]) => void }>,
) => JSX.Element;

export default SelectList;
